import React, { useMemo, useEffect, useState } from "react";

import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

import DataTableHeadCell from "lib/Tables/DataTable/DataTableHeadCell";
import MDButton from "../../../components/MDButton";
import SimpleBar from "simplebar-react";
import { Menu, MenuItem, TableFooter } from "@mui/material";

interface Props {
  entriesPerPage?: false | { defaultValue: number; entries: number[] };
  canSearch?: boolean;
  showTotalEntries?: boolean;
  table: { columns: { [key: string]: any }[]; rows: { [key: string]: any }[] };
  pagination?: { variant: "contained" | "gradient"; color: string };
  paginationEnabled?: boolean;
  isSorted?: boolean;
  noEndBorder?: boolean;
  selectedItem?: any;
  onSelectItem?: any;
  hasFilterComponent?: boolean;
  filterComponent?: React.ReactNode;
  hasHeaders?: boolean;
  tableHeaders?: React.ReactNode;
  exportExcel?: boolean;
  onExport?: any;
  dateFilter?: any;
  getRowId?: (row: any) => any;
  selectAllRows?: any;
  fontSize: any;
  totalsRow?: Record<string, any>;
  totalColumns?: string[];
  showTotals?: boolean;
  totalsFormatting?: {
    integerColumns?: string[];
    currencyColumns?: string[];
  };
  getRowColor: any;
  enableScroll?: boolean;
  scrollHeight?: string;
  onRowDoubleClick?: (row: any) => void;
  enableMultiFormatExport?: boolean;
}

const MemoizedTableRow = React.memo(
  ({
    row,
    key,
    noEndBorder,
    selectedItem,
    onSelectItem,
    getRowId,
    fontSize,
    getRowColor,
    onRowDoubleClick,
  }: any) => {
    const rowId = getRowId ? getRowId(row.original) : row.original.id;
    const color = getRowColor !== undefined ? getRowColor(row.original) : undefined;
    const conditionalStyles = getRowColor
      ? {
          backgroundColor: selectedItem.includes(rowId) ? "rgba(47, 146, 159, 0.5)" : color,
          "&:not(:last-of-type)": {
            borderBottom: "2px solid #f5f5f5",
          },
        }
      : {};

    const handleDoubleClick = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (onRowDoubleClick && row.original) {
        onRowDoubleClick(row.original);
      }
    };

    return (
      <TableRow
        key={key}
        {...row.getRowProps()}
        onDoubleClick={onRowDoubleClick ? handleDoubleClick : undefined}
        sx={{
          ...conditionalStyles,
          cursor: onRowDoubleClick ? "pointer" : "default",
          userSelect: onRowDoubleClick ? "none" : "auto",
        }}
      >
        {row.cells.map((cell: any, key: any) => {
          return (
            <td {...cell.getCellProps()} key={key}>
              <MDBox display={"flex"} alignItems={"center"} fontSize={fontSize || "15px"}>
                {key === 0 && onSelectItem ? (
                  <Checkbox
                    checked={selectedItem?.includes(rowId)}
                    onChange={() => onSelectItem(rowId)}
                    sx={{
                      paddingLeft: "0px",
                      "& .MuiSvgIcon-root": {
                        backgroundColor:
                          getRowColor !== undefined ? "white !important" : "transparent",
                      },
                    }}
                  />
                ) : null}
                {cell.column.Cell ? cell.column.Cell(cell) : cell.value}
              </MDBox>
            </td>
          );
        })}
      </TableRow>
    );
  }
);

const TotalsRow = ({ columns, totalsRow, totalColumns, totalsFormatting }: any) => {
  if (!totalsRow || !totalColumns) return null;

  return (
    <TableRow
      style={{
        fontWeight: "bold",
        backgroundColor: "#f9f9f9",
        borderTop: "2px solid #ddd",
        height: "45px",
      }}
    >
      {columns.map((col: any, idx: number) => {
        if (idx === 0 && col.hasOwnProperty("allowSelection")) {
          return <td key={idx}></td>;
        }

        let align: "left" | "right" = "left";
        let padding = "0 0 0 12px";
        let formatAsInteger = false;
        let formatAsCurrency = false;

        const shouldShowTotal = totalColumns.includes(col.accessor);

        if (shouldShowTotal) {
          align = "right";
          padding = "0 16px 0 0";
          formatAsInteger = totalsFormatting?.integerColumns?.includes(col.accessor) || false;
          formatAsCurrency = totalsFormatting?.currencyColumns?.includes(col.accessor) || false;
        }

        const isFirstNonCheckboxColumn =
          (idx === 0 && !col.hasOwnProperty("allowSelection")) ||
          (idx === 1 && columns[0].hasOwnProperty("allowSelection"));

        if (isFirstNonCheckboxColumn) {
          return (
            <td key={col.accessor || idx}>
              <div
                style={{
                  textAlign: "left",
                  padding: "0 0 0 16px",
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                TOTALS:
              </div>
            </td>
          );
        }

        if (shouldShowTotal) {
          const value = totalsRow[col.accessor];

          let formattedValue;
          if (value == null || value === undefined) {
            formattedValue = "";
          } else if (formatAsInteger) {
            formattedValue = value.toLocaleString("en-US");
          } else if (formatAsCurrency) {
            formattedValue = `$${value.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          } else {
            formattedValue = value.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          }

          return (
            <td key={col.accessor}>
              <div
                style={{
                  // textAlign: align,
                  padding: padding,
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                {formattedValue}
              </div>
            </td>
          );
        }

        return <td key={col.accessor || idx}></td>;
      })}
    </TableRow>
  );
};

function InnerTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  paginationEnabled,
  isSorted,
  noEndBorder,
  selectedItem,
  onSelectItem,
  hasFilterComponent,
  filterComponent,
  hasHeaders,
  tableHeaders,
  exportExcel,
  dateFilter,
  onExport,
  getRowId,
  getRowColor,
  selectAllRows,
  fontSize,
  enableScroll,
  totalsRow,
  totalColumns,
  showTotals = true,
  totalsFormatting,
  scrollHeight,
  onRowDoubleClick,
  enableMultiFormatExport,
}: Props): JSX.Element {
  let defaultValue: any;
  let entries: any[];

  if (entriesPerPage) {
    defaultValue = entriesPerPage.defaultValue || 10;
    entries = entriesPerPage.entries || ["100", "500", "1000"];
  }

  const columns = useMemo<any>(() => table.columns, [table]);
  const data = useMemo<any>(() => table.rows, [table]);

  const pageIndexRef = React.useRef(0);
  const pageSizeRef = React.useRef(defaultValue || 10);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: pageIndexRef.current,
        pageSize: pageSizeRef.current,
      },
    },
    useGlobalFilter,
    useSortBy,
    ...(paginationEnabled ? [usePagination] : [])
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  }: any = tableInstance;

  useEffect(() => {
    pageIndexRef.current = pageIndex;
  }, [pageIndex]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    if (paginationEnabled && pageSizeRef.current !== defaultValue) {
      setPageSize(pageSizeRef.current);
    }
  }, [paginationEnabled]);

  const [search, setSearch] = useState(globalFilter);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(anchorEl);

  const onSearchChange = useAsyncDebounce((value: any) => {
    setGlobalFilter(value || undefined);
  }, 500);

  const setSortedValue = (column: any) => {
    if (isSorted && column.isSorted) {
      return column.isSortedDesc ? "desc" : "asce";
    }
    return isSorted ? "none" : false;
  };

  const MAX_VISIBLE = 20;

  const entriesStart = paginationEnabled
    ? pageIndex === 0
      ? pageIndex + 1
      : pageIndex * pageSize + 1
    : 1;
  const entriesEnd = paginationEnabled
    ? pageIndex === 0
      ? pageSize
      : pageIndex === pageOptions?.length - 1
      ? rows.length
      : pageSize * (pageIndex + 1)
    : rows.length;

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!enableMultiFormatExport) {
      handleExport();
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExport = () => {
    onExport(tableInstance.rows);
  };

  const handleExportExcel = () => {
    if (onExport) {
      onExport(tableInstance.rows, "excel");
    }
    handleExportClose();
  };

  const handleExportPdf = () => {
    if (onExport) {
      onExport(tableInstance.rows, "pdf");
    }
    handleExportClose();
  };

  const renderPagination = pageOptions
  ?.filter(
    (p: any) =>
      p >= pageIndex - Math.floor(5 / 2) &&
      p <= pageIndex + Math.floor(MAX_VISIBLE / 2)
  )
  .map((option: any) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(option)}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));


  const shouldShowTotals =
    showTotals && totalsRow && totalColumns && Object.keys(totalsRow).length > 0;
  const tableContent = (
    <TableContainer
      sx={{
        minWidth: "max-content",
        width: "fit-content",
        boxShadow: "none",
        paddingLeft: hasHeaders ? 5 : 0,
        ...(enableScroll &&
          shouldShowTotals && {
            display: "flex",
            flexDirection: "column",
            height: scrollHeight || "62vh",
          }),
      }}
      className={typeof getRowColor === "undefined" ? "alternate-color-table" : undefined}
    >
      <Table stickyHeader {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup: any, key: any) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any, key: any) => (
                <th key={key}>
                  <MDBox display={"flex"}>
                    {column.hasOwnProperty("allowSelection") && (
                      <Checkbox
                        defaultChecked={column.allowSelection}
                        onChange={(e) => {
                          if (selectAllRows) {
                            selectAllRows(e.target.checked);
                          }
                        }}
                      />
                    )}
                    <DataTableHeadCell
                      key={key}
                      {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                      align={column.align ? column.align : "left"}
                      sorted={setSortedValue(column)}
                      minWidth={column.minWidth ? column.minWidth : "80px"}
                      style={{
                        marginLeft: column.allowSelection ? "-20px" : "0",
                        width: column.width ? column.width : "80px",
                      }}
                    >
                      {column.render("Header")}
                    </DataTableHeadCell>
                  </MDBox>
                </th>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody
          {...getTableBodyProps()}
          sx={{
            ...(enableScroll &&
              showTotals && {
                flex: 1,
                overflowY: "auto",
              }),
          }}
        >
          {(paginationEnabled ? page : rows).map((row: any, key: any) => {
            prepareRow(row);
            return (
              <MemoizedTableRow
                key={key}
                row={row}
                noEndBorder={noEndBorder}
                selectedItem={selectedItem}
                onSelectItem={onSelectItem}
                getRowId={getRowId}
                fontSize={fontSize}
                getRowColor={getRowColor}
                onRowDoubleClick={onRowDoubleClick}
              />
            );
          })}
        </TableBody>
        {shouldShowTotals && (
          <TableFooter
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#f9f9f9",
              zIndex: 1,
              borderTop: "2px solid #ddd",
            }}
          >
            <TotalsRow
              columns={columns}
              totalsRow={totalsRow}
              totalColumns={totalColumns}
              totalsFormatting={totalsFormatting}
            />
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );

  return (
    <>
      <MDBox>
        <MDBox display="flex" justifyContent={"flex-end"} pb={1} px={3} pt={3}>
          {hasHeaders && tableHeaders}
        </MDBox>

        {hasFilterComponent || canSearch ? (
          <MDBox display="flex" justifyContent="flex-end" alignItems="center" pr={3} pb={3}>
            {hasFilterComponent && filterComponent}
            {canSearch && (
              <MDBox width="15rem" ml={2} mr={2}>
                <MDInput
                  placeholder="Search..."
                  value={search}
                  size="small"
                  fullWidth
                  onChange={({ currentTarget }: any) => {
                    setSearch(search);
                    onSearchChange(currentTarget.value);
                  }}
                />
              </MDBox>
            )}
            {dateFilter && dateFilter}
            {exportExcel && (
              <>
                <MDButton
                  size={"small"}
                  variant="contained"
                  color="primary"
                  onClick={handleExportClick}
                  sx={{ marginLeft: 2, minWidth: "80px" }}
                >
                  Export
                  <Icon fontSize={"large"}>download</Icon>&nbsp;
                </MDButton>
                {enableMultiFormatExport && (
                  <Menu
                    anchorEl={anchorEl}
                    open={exportMenuOpen}
                    onClose={handleExportClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem onClick={handleExportExcel}>
                      <Icon sx={{ mr: 1 }}>table_chart</Icon>
                      Excel
                    </MenuItem>
                    <MenuItem onClick={handleExportPdf}>
                      <Icon sx={{ mr: 1 }}>picture_as_pdf</Icon>
                      PDF
                    </MenuItem>
                  </Menu>
                )}
              </>
            )}
          </MDBox>
        ) : null}
      </MDBox>

      {enableScroll ? (
        <MDBox sx={{ width: "100%", overflowX: enableScroll ? "auto" : "visible" }}>
          <SimpleBar style={{ maxHeight: scrollHeight || "62vh" }} autoHide={false}>
            {tableContent}
          </SimpleBar>
        </MDBox>
      ) : (
        <TableContainer
          sx={{ boxShadow: "none", paddingLeft: hasHeaders ? 5 : 0 }}
          className={typeof getRowColor === "undefined" ? "alternate-color-table" : undefined}
        >
          <Table {...getTableProps()}>
            <MDBox component="thead">
              {headerGroups.map((headerGroup: any, key: any) => (
                <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any, key: any) => (
                    <th key={key}>
                      <MDBox display={"flex"}>
                        {column.hasOwnProperty("allowSelection") && (
                          <Checkbox
                            defaultChecked={column.allowSelection}
                            onChange={(e) => {
                              if (selectAllRows) {
                                selectAllRows(e.target.checked);
                              }
                            }}
                          />
                        )}
                        <DataTableHeadCell
                          key={key}
                          {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                          align={column.align ? column.align : "left"}
                          sorted={setSortedValue(column)}
                          minWidth={column.minWidth ? column.minWidth : "80px"}
                          style={{
                            marginLeft: column.allowSelection ? "-20px" : "0",
                            width: column.width ? column.width : "80px",
                          }}
                        >
                          {column.render("Header")}
                        </DataTableHeadCell>
                      </MDBox>
                    </th>
                  ))}
                </TableRow>
              ))}
            </MDBox>
            <TableBody {...getTableBodyProps()}>
              {(paginationEnabled ? page : rows).map((row: any, key: any) => {
                prepareRow(row);
                return (
                  <MemoizedTableRow
                    key={key}
                    row={row}
                    noEndBorder={noEndBorder}
                    selectedItem={selectedItem}
                    onSelectItem={onSelectItem}
                    getRowId={getRowId}
                    fontSize={fontSize}
                    getRowColor={getRowColor}
                    onRowDoubleClick={onRowDoubleClick}
                  />
                );
              })}
            </TableBody>
            {shouldShowTotals && (
              <TableFooter>
                <TotalsRow
                  columns={columns}
                  totalsRow={totalsRow}
                  totalColumns={totalColumns}
                  totalsFormatting={totalsFormatting}
                />
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      )}

      {paginationEnabled && (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={!showTotalEntries && pageOptions?.length === 1 ? 0 : 3}
        >
          {showTotalEntries && (
            <div className={"d-flex"}>
              <MDBox mb={{ xs: 3, sm: 0 }}>
                <MDTypography variant="button" color="secondary" fontWeight="regular">
                  Showing {entriesStart} to {entriesEnd} of {rows.length} entries
                </MDTypography>
              </MDBox>
              {entriesPerPage && (
                <MDBox display="flex" alignItems="center">
                  <Autocomplete
                    disableClearable
                    value={pageSize.toString()}
                    options={entries}
                    onChange={(event, newValue) => {
                      setPageSize(parseInt(newValue, 10));
                    }}
                    size="small"
                    sx={{ width: "5rem", marginLeft: "10px" }}
                    renderInput={(params) => <MDInput {...params} />}
                  />
                  <MDTypography variant="caption" color="secondary">
                    &nbsp;&nbsp;entries per page
                  </MDTypography>
                </MDBox>
              )}
            </div>
          )}
          {pageOptions?.length > 1 && (
            <MDPagination
              variant={pagination?.variant || "gradient"}
              color={pagination?.color || "info"}
            >
              {canPreviousPage && (
                <MDPagination item onClick={() => previousPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination}
              {canNextPage && (
                <MDPagination item onClick={() => nextPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      )}
    </>
  );
}

InnerTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: ["5", "10", "15", "20", "25"] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "primary" },
  isSorted: true,
  noEndBorder: false,
  paginationEnabled: false,
  fontSize: null,
  getRowColor: undefined,
  enableScroll: false,
  showTotals: true,
  scrollHeight: "62vh",
  onRowDoubleClick: undefined,
  enableMultiFormatExport: false,
};

export default React.memo(InnerTable);
